import { createLookupTable } from "/lib";
import { BikeAvailabilityStatus, BikeModel, getBikeModelAvailability } from "/lib/bike";
import { BikeModelsQ } from "/lib/sanity/queries/bikeModels";
import { ImageQ } from "/lib/sanity/queries/common";
import { Subscription } from "/lib/subscription";
import { LONG_BIKE_SLUG_ORDER } from "/lib/config";

export type Variant = {
  type: "short" | "long";
  title: string;
  description: string;
  passengers: number;
};

export const BIKE_VARIANTS: Record<"long" | "short", Variant> = {
  long: {
    type: "long",
    title: "Lange modeller:",
    description: "Plass til last og 2 passasjerer",
    passengers: 2
  },
  short: {
    type: "short",
    title: "Korte modeller:",
    description: "Plass til last og 1 passasjer",
    passengers: 1
  }
};

export type MassagedBikeAndSub = Omit<BikeModel, "subscriptions"> & {
  subscriptions: Extract<Subscription, { type: "bike" }>[] | null;
  image: ImageQ;
  pageLink: {
    href: string;
  };
  availability: BikeAvailabilityStatus;
  slug: string;
};

// Utility function to please typescript
function removeAccessory(val: Subscription): val is Extract<Subscription, { type: "bike" }> {
  // This is to make sure the e2e tests are working.
  // It happens because the tests get mockData that could be better
  if (typeof val === "undefined") {
    return false;
  }
  return val.type !== "accessory";
}

export const massageBikeAndSubData = (
  bikes: BikeModel[],
  subs: Subscription[],
  bikeModelsTable: Record<string, BikeModelsQ[0]>,
  availabilityTable: Record<string, BikeAvailabilityStatus>
) => {
  // Creates lookup table for subs
  const subsTable: Record<string, Subscription> = createLookupTable(subs, "id");

  const outputData: MassagedBikeAndSub[] = bikes
    .filter((bike) => {
      if (bike.id in bikeModelsTable) {
        return true;
      }
      return false;
    })
    .map((bike) => {
      const image: ImageQ = bikeModelsTable[bike.id].image;

      const pageLink: { href: string } = {
        href: bikeModelsTable[bike.id]["linkItems"]?.[0]["href"] || ""
      };

      const availability = availabilityTable[bike.id];
      // Extract slug from the last segment of pageLink href
      const hrefParts = (pageLink.href || "").split("/").filter(Boolean);
      const slug = hrefParts[hrefParts.length - 1] || "";

      if (!bike.subscriptions) {
        return {
          ...bike,
          subscriptions: null,
          image: image,
          pageLink: pageLink,
          availability: availability,
          slug: slug
        };
      }

      const subscriptions = bike.subscriptions
        .map((id) => subsTable[id])
        .filter(removeAccessory)
        .filter((sub) => !sub.archived)
        .sort((a, b) => a.price - b.price);

      return {
        ...bike,
        subscriptions: subscriptions,
        image: image,
        pageLink: pageLink,
        availability: availability,
        slug: slug
      };
    });

  // Sort the bikes by the lowest subscription price
  // At this point, we know that the first element of subscriptions is the lowest price for each bike type.
  outputData.sort(
    (a, b) => (a.subscriptions?.at(0)?.price ?? 0) - (b.subscriptions?.at(0)?.price ?? 0)
  );

  return outputData;
};

/**
 * Custom sort order for long bikes
 * Bikes are matched by slug (from environment variable)
 */
function getLongBikeSlugOrder(): string[] {
  if (!LONG_BIKE_SLUG_ORDER) {
    return [];
  }
  return LONG_BIKE_SLUG_ORDER.split(",")
    .map((slug) => slug.trim())
    .filter(Boolean);
}

function getLongBikeSortIndex(bike: MassagedBikeAndSub): number {
  const slugOrder = getLongBikeSlugOrder();
  const bikeSlug = bike.slug.toLowerCase();

  for (let i = 0; i < slugOrder.length; i++) {
    const orderSlug = slugOrder[i].toLowerCase();
    if (bikeSlug === orderSlug) {
      return i;
    }
  }

  // Bikes not in the order list go to the end
  return slugOrder.length;
}

export function compareLongBikes(a: MassagedBikeAndSub, b: MassagedBikeAndSub): number {
  const slugOrder = getLongBikeSlugOrder();
  const indexA = getLongBikeSortIndex(a);
  const indexB = getLongBikeSortIndex(b);

  // If both bikes are in the custom order, sort by their index
  if (indexA < slugOrder.length && indexB < slugOrder.length) {
    return indexA - indexB;
  }

  // If only one is in the order, prioritize it
  if (indexA < slugOrder.length) return -1;
  if (indexB < slugOrder.length) return 1;

  // If neither is in the order, maintain original order (or sort by price as fallback)
  return (a.subscriptions?.at(0)?.price ?? 0) - (b.subscriptions?.at(0)?.price ?? 0);
}

export function createAvailabilityLookupTable(
  bikeModels: BikeModel[]
): Record<string, BikeAvailabilityStatus> {
  const table: ReturnType<typeof createAvailabilityLookupTable> = {};

  for (const bike of bikeModels) {
    table[bike.id] = getBikeModelAvailability(bike);
  }

  return table;
}
